import { HttpBackend, HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { LocalStorageService } from "angular-web-storage";
import { AsyncSubject, forkJoin } from "rxjs";
import { environment } from "src/environments/environment";

export interface WidgetRefreshContext {
  widgetSettingId: number;
  pageId: number;
  pageNumber: number;
}

interface PendingWidgetSaveBatch {
  context: WidgetRefreshContext;
  headers: HttpHeaders;
  requestCompletions: AsyncSubject<boolean>[];
}

export interface WidgetSaveRequestHandle {
  completion: AsyncSubject<boolean>;
}

@Injectable({ providedIn: "root" })
export class WidgetRefreshCoordinatorService {
  private readonly markedSavePayloads = new WeakMap<
    object,
    WidgetRefreshContext
  >();
  private readonly registeringWidgetSaveBatches = new Map<
    string,
    PendingWidgetSaveBatch
  >();
  private readonly http: HttpClient;

  constructor(
    private storage: LocalStorageService,
    httpBackend: HttpBackend,
  ) {
    this.http = new HttpClient(httpBackend);
  }

  markWidgetSave(payload: any): any {
    const selectedWidget = this.storage.get("selectedwidget");
    if (
      payload &&
      typeof payload === "object" &&
      selectedWidget?.widgetSettingId &&
      selectedWidget?.pageId &&
      selectedWidget?.pageNumber
    ) {
      this.markedSavePayloads.set(payload, {
        widgetSettingId: selectedWidget.widgetSettingId,
        pageId: selectedWidget.pageId,
        pageNumber: selectedWidget.pageNumber,
      });
    }
    return payload;
  }

  getMarkedWidgetContext(payload: any): WidgetRefreshContext | undefined {
    if (!payload || typeof payload !== "object") {
      return undefined;
    }
    return this.markedSavePayloads.get(payload);
  }

  beginWidgetSave(
    context: WidgetRefreshContext,
    headers: HttpHeaders,
  ): WidgetSaveRequestHandle {
    const key = this.getContextKey(context);
    let batch = this.registeringWidgetSaveBatches.get(key);
    if (!batch) {
      batch = {
        context,
        headers,
        requestCompletions: [],
      };
      this.registeringWidgetSaveBatches.set(key, batch);

      // HttpClient subscriptions are started synchronously by a widget's Save
      // handler. Closing registration on the next event-loop turn captures all
      // requests from that Save action without mixing in a later Save click.
      setTimeout(() => this.closeWidgetSaveBatch(key, batch), 0);
    }

    batch.headers = headers;
    const completion = new AsyncSubject<boolean>();
    batch.requestCompletions.push(completion);
    return { completion };
  }

  completeWidgetSave(
    requestHandle: WidgetSaveRequestHandle,
    succeeded: boolean,
  ): void {
    if (!requestHandle) {
      return;
    }

    requestHandle.completion.next(succeeded);
    requestHandle.completion.complete();
  }

  private closeWidgetSaveBatch(
    key: string,
    batch: PendingWidgetSaveBatch,
  ): void {
    if (this.registeringWidgetSaveBatches.get(key) !== batch) {
      return;
    }

    this.registeringWidgetSaveBatches.delete(key);
    if (batch.requestCompletions.length === 0) {
      return;
    }

    forkJoin(batch.requestCompletions).subscribe({
      next: (requestResults) => {
        if (requestResults.some((succeeded) => !succeeded)) {
          return;
        }

        this.http
          .post(
            environment.baseURL + "widgets/notifyWidgetRefresh",
            batch.context,
            { headers: batch.headers },
          )
          .subscribe({
            error: (error) =>
              console.error(
                "Unable to notify display about widget update",
                error,
              ),
          });
      },
    });
  }

  private getContextKey(context: WidgetRefreshContext): string {
    return `${context.pageId}:${context.pageNumber}:${context.widgetSettingId}`;
  }
}
